import { OnChanges } from '@angular/core';
import { EventEmitter } from '@angular/core';
import { Output } from '@angular/core';
import { Input } from '@angular/core';
import { Component } from '@angular/core';
import { PromotionalType } from 'app/modules/main/models/publisher-tags-and-promotional-types';
import { PublisherTagsAndPromotionalTypesSelectors } from 'app/modules/communication-centre/advertiser/store/publisher-tags-and-promotional-types/publisher-tags-and-promotional-types.selectors';
import { Localisation } from 'app/modules/main/components/data-selector/data-selector.models';
import { SelectableData } from 'app/modules/main/components/data-selector/data-selector.models';
import { PromotionalTypeColumnExtractor } from 'app/modules/main/components/promotional-type-selector/promotional-type-column-extractor.service';
import { PromotionalTypeGroupFilter } from 'app/modules/main/components/promotional-type-selector/promotional-type-group-filter.service';
import { PromotionalTypeSelectorMapper } from 'app/modules/main/components/promotional-type-selector/promotional-type-selector-mapper';
import { BehaviorSubject } from 'rxjs';
import { take } from 'rxjs/operators';
import { map } from 'rxjs/operators';

@Component({
  selector: 'app-promotional-type-selector',
  templateUrl: './promotional-type-selector.component.html',
})
export class PromotionalTypeSelectorComponent implements OnChanges {

  @Input() readonly open: boolean = false;
  @Input() readonly required: boolean = false;
  @Input() readonly singular: boolean = false;

  @Input() readonly localisation?: Localisation = {
    title: $localize`:@@communication-centre.advertiser.promotional-type-selector.title:Select Promotional Types`,
    closeLabel: $localize`:@@communication-centre.advertiser.promotional-type-selector.label.close:Close`,
    searchLabel: $localize`:@@communication-centre.advertiser.promotional-type-selector.label.search:Search`,
    searchPlaceholder: $localize`:@@communication-centre.advertiser.promotional-type-selector.label.search-placeholder:Type`,
    allCategoriesLabel: $localize`:@@communication-centre.advertiser.promotional-type-selector.label.all-categories:All`,
    selectAllLabel: $localize`:@@communication-centre.advertiser.promotional-type-selector.label.select-all:All`,
    selectNoneLabel: $localize`:@@communication-centre.advertiser.promotional-type-selector.label.select-none:None`,
    itemHeaderLabel: $localize`:@@communication-centre.advertiser.promotional-type-selector.label.item-header:Item`,
    selectHeaderLabel: $localize`:@@communication-centre.advertiser.promotional-type-selector.label.select-header:Select`,
    noDataText: $localize`:@@communication-centre.advertiser.promotional-type-selector.no-data:You have no promotions`,
    noMatchesText: $localize`:@@communication-centre.advertiser.promotional-type-selector.no-matches:Your filter criteria did not return any results.`,
    cancelLabel: $localize`:@@communication-centre.advertiser.promotional-type-selector.button.cancel:Cancel`,
    submitLabel: $localize`:@@communication-centre.advertiser.promotional-type-selector.button.submit:Select`,
  };

  @Input() readonly selectedPromotionalTypes: PromotionalType[];

  @Output() readonly submitted = new EventEmitter<PromotionalType[]>();
  @Output() readonly cancelled = new EventEmitter<void>();

  private initialized = false;

  readonly selectablePromotionalTypes = new BehaviorSubject<SelectableData<PromotionalType>[]>(null);

  constructor(
    readonly promotionalTypeColumnExtractor: PromotionalTypeColumnExtractor,
    readonly promotionalTypeGroupFilter: PromotionalTypeGroupFilter,
    private readonly promotionalTypeSelectorMapper: PromotionalTypeSelectorMapper,
    private readonly publisherTagsAndPromotionalTypesSelectors: PublisherTagsAndPromotionalTypesSelectors,
  ) {
  }

  ngOnChanges(): void {

    if (this.initialized || this.selectedPromotionalTypes === null) {
      return;
    }

    this.initialized = true;

    this.publisherTagsAndPromotionalTypesSelectors.getPublisherTagsAndPromotionalTypes()
      .pipe(
        take(1),
        map(publisherTagsAndPromotionalTypes => publisherTagsAndPromotionalTypes.promotionalTypes),
        map(promotionalTypes => this.promotionalTypeSelectorMapper.toSelectablePromotionalTypes(promotionalTypes, this.selectedPromotionalTypes)),
      )
      .subscribe(selectablePromotionalTypes => this.selectablePromotionalTypes.next(selectablePromotionalTypes));
  }

  public cancel(): void {
    this.cancelled.emit();
  }

  public submit(
    selectedPromotionalTypes: SelectableData<PromotionalType>[]
  ): void {

    const promotionalTypes = this.promotionalTypeSelectorMapper.toPromotionalTypes(selectedPromotionalTypes);

    this.submitted.emit(promotionalTypes);
  }

}
